<script setup lang="ts">
/**
 * Module: web/src/views/LoginView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.2.0
 * Date: 2026-07-14
 *
 * v1.2.0 (brand logo -- Robert, 2026-07-19): the login hero is now the
 * horizontal "Commercial Towing" one-line badge (settings.brandLogo) replacing
 * the generic accent shield-icon placeholder + the stacked wordmark/subline; a
 * small muted "Operations Portal" line stays under it so the sign-in target is
 * still named. The logo reads on both themes via its navy outline (see the
 * settings-store note).
 *
 * v1.1.0 (Remember Me, decision #167): "Trust this device for 30 days"
 * checkbox under the password field -- opts the session into the
 * trusted-device lifetime (30d absolute / 7d idle vs the 12h/2h default)
 * so dispatch machines stop re-logging multiple times a day. Full logins
 * only; the PIN page has no such option by design.
 *
 * Mobile-FIRST login (doc 04 sect 6.1 + doc 12 sect 4.1 -- night-shift
 * dispatchers log in from a phone). Single-column form, 48px targets.
 * Error states: 401 {code:'invalid'} generic message; 423 {code:'locked'}
 * lockout countdown from unlock_at. Links: Quick PIN access + Forgot password.
 */
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useSettingsStore } from '@/stores/settings';
import { useLockout } from '@/composables/useLockout';
import { ApiError } from '@/composables/useApi';
import AppIcon from '@/components/AppIcon.vue';

const auth = useAuthStore();
const settings = useSettingsStore();
const route = useRoute();
const router = useRouter();
const { lockedOut, remainingLabel, startLockout } = useLockout();

const email = ref('');
const password = ref('');
const remember = ref(false);
const showPassword = ref(false);
const submitting = ref(false);
const errorMessage = ref('');

async function submit() {
  if (submitting.value || lockedOut.value) return;
  errorMessage.value = '';
  if (!email.value.trim() || !password.value) {
    errorMessage.value = 'Enter your email and password.';
    return;
  }
  submitting.value = true;
  try {
    const session = await auth.login(email.value.trim(), password.value, remember.value);
    if (session.must_change_password) {
      void router.push('/change-password');
      return;
    }
    const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : null;
    void router.push(redirect && redirect.startsWith('/') ? redirect : auth.landingPath);
  } catch (e) {
    if (e instanceof ApiError && e.status === 423) {
      startLockout(e.field('unlock_at'));
      errorMessage.value = '';
    } else if (e instanceof ApiError && e.status === 401) {
      errorMessage.value = 'Invalid email or password.';
    } else {
      errorMessage.value = 'Sign-in failed. Check your connection and try again.';
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="min-h-screen flex flex-col items-center justify-center px-4 py-8">
    <div class="w-full max-w-sm">
      <!-- Brand -->
      <div class="text-center mb-8">
        <img
          :src="settings.brandLogo"
          :alt="settings.brandSubline"
          class="mx-auto w-full max-w-[300px] h-auto object-contain select-none"
          draggable="false"
        />
        <p class="text-sm text-fg-muted mt-3">Operations Portal</p>
      </div>

      <form class="space-y-4" novalidate @submit.prevent="submit">
        <div v-if="lockedOut" class="alert-error flex items-start gap-2.5" role="alert">
          <AppIcon name="lock" class="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>
            Account locked after too many attempts.
            <span class="font-semibold whitespace-nowrap">Try again in {{ remainingLabel }}.</span>
          </span>
        </div>
        <div v-else-if="errorMessage" class="alert-error" role="alert">{{ errorMessage }}</div>

        <div>
          <label class="field-label" for="login-email">Email</label>
          <input
            id="login-email"
            v-model="email"
            class="field-input"
            type="email"
            name="email"
            autocomplete="username"
            inputmode="email"
            autocapitalize="none"
            spellcheck="false"
            placeholder="you@commercialtowingservices.com"
            :disabled="submitting || lockedOut"
          />
        </div>

        <div>
          <label class="field-label" for="login-password">Password</label>
          <div class="relative">
            <input
              id="login-password"
              v-model="password"
              class="field-input pr-12"
              :type="showPassword ? 'text' : 'password'"
              name="password"
              autocomplete="current-password"
              :disabled="submitting || lockedOut"
            />
            <button
              type="button"
              class="absolute inset-y-0 right-0 px-3 text-fg-muted hover:text-fg"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
              @click="showPassword = !showPassword"
            >
              <AppIcon :name="showPassword ? 'eye-off' : 'eye'" class="w-5 h-5" />
            </button>
          </div>
        </div>

        <label class="flex items-center gap-2.5 min-h-11 text-sm text-fg cursor-pointer select-none">
          <input
            v-model="remember"
            type="checkbox"
            class="w-5 h-5 accent-[var(--accent,#2563eb)]"
            :disabled="submitting || lockedOut"
          />
          Trust this device for 30 days
        </label>

        <button type="submit" class="btn-primary w-full" :disabled="submitting || lockedOut">
          {{ submitting ? 'Signing in…' : 'Sign in' }}
        </button>
      </form>

      <div class="mt-6 space-y-3">
        <RouterLink
          to="/pin"
          class="btn-secondary w-full"
        >
          <AppIcon name="key" class="w-5 h-5" /> Quick PIN access
        </RouterLink>
        <div class="text-center">
          <RouterLink to="/reset-request" class="inline-block py-2.5 px-3 text-sm text-accent hover:underline">
            Forgot password?
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>
